import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type EmailEventType =
  | 'WELCOME_VERIFY'
  | 'WELCOME_SSO'
  | 'ORDER_CONFIRMED'
  | 'ORDER_SHIPPED'
  | 'ORDER_DELIVERED'
  | 'ORDER_CANCELLED'
  | 'PASSWORD_RESET';

export const EMAIL_EVENT_LABELS: Record<EmailEventType, string> = {
  WELCOME_VERIFY: 'Registrazione (verifica email)',
  WELCOME_SSO: 'Registrazione (Google/Apple)',
  ORDER_CONFIRMED: 'Ordine confermato',
  ORDER_SHIPPED: 'Ordine spedito',
  ORDER_DELIVERED: 'Ordine consegnato',
  ORDER_CANCELLED: 'Ordine annullato',
  PASSWORD_RESET: 'Recupero password',
};

export interface EmailTemplate {
  id: string;
  tenantId: string;
  eventType: EmailEventType;
  lang: string;
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  placeholders: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ResolvedEmailTemplate {
  fromDefault: boolean;
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  placeholders: string[];
  isActive: boolean;
}

export interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
}

export interface UpsertEmailTemplateInput {
  eventType: EmailEventType;
  lang?: 'it' | 'en';
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  placeholders: string[];
  isActive?: boolean;
}

export function useListEmailTemplates() {
  return useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const res = await apiClient.get<EmailTemplate[]>('/admin/email-templates');
      return res.data;
    },
  });
}

export function useEmailTemplate(eventType: EmailEventType, lang: 'it' | 'en') {
  return useQuery({
    queryKey: ['email-template', eventType, lang],
    queryFn: async () => {
      const res = await apiClient.get<ResolvedEmailTemplate>(
        `/admin/email-templates/${eventType}/${lang}`,
      );
      return res.data;
    },
  });
}

export function useUpsertEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertEmailTemplateInput) => {
      const res = await apiClient.put<EmailTemplate>('/admin/email-templates', input);
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['email-templates'] });
      qc.invalidateQueries({ queryKey: ['email-template', data.eventType, data.lang] });
    },
  });
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/email-templates/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-templates'] });
      qc.invalidateQueries({ queryKey: ['email-template'] });
    },
  });
}

export function usePreviewEmailTemplate() {
  return useMutation({
    mutationFn: async (input: Omit<UpsertEmailTemplateInput, 'isActive'> & { vars?: Record<string, string | number> }) => {
      const res = await apiClient.post<RenderedEmail>('/admin/email-templates/preview', input);
      return res.data;
    },
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: async (input: { eventType: EmailEventType; lang?: 'it' | 'en'; to?: string }) => {
      const res = await apiClient.post<{ sent: true }>('/admin/email-templates/send-test', input);
      return res.data;
    },
  });
}

export interface UserProvidersResponse {
  providers: string[];
  canResetPassword: boolean;
}

export function useUserProviders(userId: string | null) {
  return useQuery({
    enabled: Boolean(userId),
    queryKey: ['user-providers', userId],
    queryFn: async () => {
      const res = await apiClient.get<UserProvidersResponse>(`/users/${userId}/providers`);
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useAdminSendPasswordReset() {
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiClient.post<{ sent: true }>(`/users/${userId}/password-reset`);
      return res.data;
    },
  });
}

/**
 * T-154 — imposta direttamente una nuova password per un tifoso email+password.
 * Solo per utenti NON SSO (il server rifiuta con 409 gli SSO-only). La password
 * viaggia solo nel body della POST e non viene mai persistita lato client.
 */
export function useAdminSetPassword() {
  return useMutation({
    mutationFn: async ({ userId, password }: { userId: string; password: string }) => {
      const res = await apiClient.post<{ set: true }>(`/users/${userId}/set-password`, {
        password,
      });
      return res.data;
    },
  });
}
